import { useCallback, useEffect, useMemo, useState } from 'react';

import type { Product } from '@atlassian/teams-common/types';

import { postCreateTeam } from '../clients/team-client';
import { trimTeamAriInObject } from '../transfomers/teams';
import { type CreatedTeamData, TeamMembershipSettings } from '../types/team';
import { type ErrorWithStatus } from '../utils/fetch';

import { useService } from './use-service';

type CreateTeamServiceType = {
	teamName: string;
	teamMembershipSettings: TeamMembershipSettings;
	teamMembers?: string[];
};

export interface UseCreateTeamServiceData {
	loading?: boolean;
	error?: ErrorWithStatus;
	data?: CreatedTeamData;
	createTeam: (
		teamName: string,
		membershipSettings: TeamMembershipSettings,
		teamMembers?: string[],
	) => void;
}

export function useCreateTeamService(
	cloudId: string,
	product: Product,
	tenantUrl?: string,
	orgId?: string,
): UseCreateTeamServiceData {
	const [teamInfo, setTeamInfo] = useState<CreateTeamServiceType>({
		teamName: '',
		teamMembershipSettings: TeamMembershipSettings.OPEN,
	});

	const requestToCreateTeam = useCallback(() => {
		const metaData = {
			product,
			cloudId,
			tenantUrl,
			orgId,
			members: teamInfo.teamMembers,
		};

		return postCreateTeam(teamInfo.teamName, teamInfo.teamMembershipSettings, metaData);
	}, [cloudId, product, tenantUrl, teamInfo, orgId]);

	const { loading, error, data, fetchData } = useService<CreatedTeamData, ErrorWithStatus>(
		requestToCreateTeam,
	);
	const memoizedData = useMemo(() => trimTeamAriInObject(data), [data]);

	// calling creating team endpoint when teamName change
	useEffect(() => {
		if (!teamInfo.teamName) {
			return;
		}

		fetchData();
	}, [fetchData, teamInfo]);

	return {
		loading,
		error,
		data: memoizedData,
		createTeam: (
			teamName: string,
			teamMembershipSettings: TeamMembershipSettings,
			teamMembers?: string[],
		) => {
			// `teamName` changes --> `requestToCreateTeam` is updated -->  `fetchData` is updated --> `memoizedData` is updated
			setTeamInfo({
				teamName,
				teamMembershipSettings,
				...(teamMembers && teamMembers.length > 0 && { teamMembers }),
			});
		},
	};
}
